import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useUser, type User } from './useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { Button } from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { Accordion, AccordionItem } from '../../../components/ui/Accordion';
import { ChevronLeftIcon } from '../../../components/ui/Icons';
import { cn } from '../../../lib/utils';
import { getVesselModulesMenuForPermissions, sidebarItems } from '../../../components/Layout/config/SidebarItems';
import { getModuleKeyForPath, LANDING_MODULE_LABELS } from '../../../config/permissionModules';

interface PermissionRow {
  moduleKey: string;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canClose: boolean;
}

type ModuleEntry = {
  key: string;
  label: string;
  icon?: React.ReactNode;
};

type PermissionNode = {
  id: string;
  title: string;
  icon?: React.ReactNode;
  entries: ModuleEntry[];
  children: PermissionNode[];
};

const toLabel = (key: string) =>
  (LANDING_MODULE_LABELS as Record<string, string>)[key] ||
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Resolve one or many module keys from a sidebar menu item (matches Sidebar permission checks). */
const resolveModuleKeys = (item: any): string[] => {
  if (Array.isArray(item?.permissionModuleKeys) && item.permissionModuleKeys.length > 0) {
    return item.permissionModuleKeys.filter((k: unknown): k is string => typeof k === 'string' && k.length > 0);
  }
  if (typeof item?.permissionModuleKey === 'string' && item.permissionModuleKey) {
    return [item.permissionModuleKey];
  }
  const path: string | undefined = item?.path ?? item?.views?.[0]?.path;
  const fromPath = path ? getModuleKeyForPath(path) : undefined;
  return fromPath ? [fromPath] : [];
};

const collectModuleKeys = (nodes: PermissionNode[], into: Set<string> = new Set()): Set<string> => {
  nodes.forEach((node) => {
    node.entries.forEach((e) => into.add(e.key));
    collectModuleKeys(node.children, into);
  });
  return into;
};

const collectNodeModuleKeys = (node: PermissionNode): string[] => {
  const keys: string[] = [];
  node.entries.forEach((e) => keys.push(e.key));
  node.children.forEach((child) => keys.push(...collectNodeModuleKeys(child)));
  return keys;
};

/**
 * Build a tree that mirrors sidebar groups and subgroups
 * (e.g. Purchasing → Supplies → Cases, Reference data → PMS → …).
 * Single-key leaves become rows on the parent; folders / multi-key items stay subgroups.
 */
const buildDynamicPermissionStructure = (): PermissionNode[] => {
  const entriesFromItem = (item: any): ModuleEntry[] => {
    const keys = resolveModuleKeys(item);
    const itemName = item?.name || 'Module';
    const multi = keys.length > 1;
    return keys.map((key) => ({
      key,
      label: multi ? toLabel(key) : itemName,
      icon: item?.icon,
    }));
  };

  const buildNodeFromItem = (item: any, pathId: string): PermissionNode | null => {
    if (item?.name === 'Bookmarks' || item?.sectionLabel) return null;

    const keys = resolveModuleKeys(item);
    const childItems: any[] = Array.isArray(item?.children) ? item.children : [];
    const itemName = item?.name || 'Module';

    const entries: ModuleEntry[] = [];
    const children: PermissionNode[] = [];

    childItems.forEach((child, idx) => {
      const childKeys = resolveModuleKeys(child);
      const grandChildren: any[] = Array.isArray(child?.children) ? child.children : [];
      const isFolder = grandChildren.length > 0;
      const isMultiKeyLeaf = childKeys.length > 1 && grandChildren.length === 0;

      if (isFolder || isMultiKeyLeaf) {
        const nested = buildNodeFromItem(child, `${pathId}/${child?.name || idx}`);
        if (nested) children.push(nested);
      } else if (childKeys.length === 1) {
        // Single-key leaf → row under this group (e.g. Due jobs under PMS)
        entries.push(...entriesFromItem(child));
      } else if (grandChildren.length === 0 && childKeys.length === 0) {
        // Empty placeholder — skip
      }
    });

    // Keys on this item itself (rare for folders)
    if (keys.length > 0 && childItems.length === 0) {
      entries.push(...entriesFromItem(item));
    } else if (keys.length > 0) {
      entries.unshift(...entriesFromItem(item));
    }

    if (entries.length === 0 && children.length === 0) return null;

    return {
      id: pathId,
      title: itemName,
      icon: item?.icon,
      entries,
      children,
    };
  };

  const roots: PermissionNode[] = [];
  const topLevelItems = [...sidebarItems, getVesselModulesMenuForPermissions()];

  topLevelItems.forEach((item, idx) => {
    if (item?.sectionLabel || item?.name === 'Bookmarks') return;

    const keys = resolveModuleKeys(item);
    const childItems: any[] = Array.isArray(item?.children) ? item.children : [];

    // Top-level leaf (Dashboard)
    if (keys.length > 0 && childItems.length === 0) {
      roots.push({
        id: `root-${idx}`,
        title: item.name || 'Module',
        icon: item.icon,
        entries: entriesFromItem(item),
        children: [],
      });
      return;
    }

    if (childItems.length === 0 && keys.length === 0) return;

    const entries: ModuleEntry[] = [];
    const children: PermissionNode[] = [];

    childItems.forEach((child, cIdx) => {
      const childKeys = resolveModuleKeys(child);
      const grandChildren: any[] = Array.isArray(child?.children) ? child.children : [];
      const isFolder = grandChildren.length > 0;
      const isMultiKeyLeaf = childKeys.length > 1 && grandChildren.length === 0;

      if (isFolder || isMultiKeyLeaf) {
        const nested = buildNodeFromItem(child, `root-${idx}/${child?.name || cIdx}`);
        if (nested) children.push(nested);
      } else if (childKeys.length >= 1) {
        entries.push(...entriesFromItem(child));
      }
    });

    if (keys.length > 0) {
      entries.unshift(...entriesFromItem(item));
    }

    if (entries.length === 0 && children.length === 0) return;

    roots.push({
      id: `root-${idx}`,
      title: item.name || 'Group',
      icon: item.icon,
      entries,
      children,
    });
  });

  return roots;
};

const filterNodeByPermissions = (
  node: PermissionNode,
  permissionsByKey: Map<string, PermissionRow>,
): PermissionNode | null => {
  const entries = node.entries.filter((e) => permissionsByKey.has(e.key));
  const children = node.children
    .map((c) => filterNodeByPermissions(c, permissionsByKey))
    .filter((c): c is PermissionNode => c != null);
  if (entries.length === 0 && children.length === 0) return null;
  return { ...node, entries, children };
};

type PermissionTableProps = {
  entries: ModuleEntry[];
  permissionsByKey: Map<string, PermissionRow>;
  colors: ReturnType<typeof useTheme>['colors'];
  onChange: (moduleKey: string, field: 'canView' | 'canEdit' | 'canDelete' | 'canClose', value: boolean) => void;
  rowKeyPrefix: string;
};

const PermissionTable: React.FC<PermissionTableProps> = ({
  entries,
  permissionsByKey,
  colors,
  onChange,
  rowKeyPrefix,
}) => {
  if (entries.length === 0) return null;
  return (
    <div className={`overflow-x-auto rounded border ${colors.border}`}>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className={colors.surfaceHover}>
            <th className="p-2 font-medium">Module</th>
            <th className="p-2 font-medium w-20 text-center">View</th>
            <th className="p-2 font-medium w-20 text-center">Edit</th>
            <th className="p-2 font-medium w-20 text-center">Delete</th>
            <th className="p-2 font-medium w-20 text-center" title="Close / finalize / post (e.g. MGA)">
              Close
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const p = permissionsByKey.get(entry.key);
            if (!p) return null;
            return (
              <tr key={`${rowKeyPrefix}:${entry.key}:${entry.label}`} className={`border-b ${colors.borderLight}`}>
                <td className="p-2">
                  <span className="inline-flex items-center gap-1.5">
                    {entry.icon ? <span className="inline-flex">{entry.icon}</span> : null}
                    <span>{entry.label}</span>
                  </span>
                </td>
                <td className="p-2 text-center">
                  <div className="flex justify-center">
                    <Switch
                      checked={p.canView}
                      onChange={(e) => onChange(entry.key, 'canView', e.target.checked)}
                      size="sm"
                    />
                  </div>
                </td>
                <td className="p-2 text-center">
                  <div className="flex justify-center">
                    <Switch
                      checked={p.canEdit}
                      disabled={!p.canView}
                      onChange={(e) => onChange(entry.key, 'canEdit', e.target.checked)}
                      size="sm"
                    />
                  </div>
                </td>
                <td className="p-2 text-center">
                  <div className="flex justify-center">
                    <Switch
                      checked={p.canDelete}
                      disabled={!p.canView}
                      onChange={(e) => onChange(entry.key, 'canDelete', e.target.checked)}
                      size="sm"
                    />
                  </div>
                </td>
                <td className="p-2 text-center">
                  <div className="flex justify-center">
                    <Switch
                      checked={Boolean(p.canClose)}
                      disabled={!p.canView}
                      onChange={(e) => onChange(entry.key, 'canClose', e.target.checked)}
                      size="sm"
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

type PermissionNodeAccordionProps = {
  node: PermissionNode;
  permissionsByKey: Map<string, PermissionRow>;
  colors: ReturnType<typeof useTheme>['colors'];
  onChange: (moduleKey: string, field: 'canView' | 'canEdit' | 'canDelete' | 'canClose', value: boolean) => void;
  onGroupToggle: (moduleKeys: string[], enabled: boolean) => void;
  depth?: number;
};

const PermissionNodeAccordion: React.FC<PermissionNodeAccordionProps> = ({
  node,
  permissionsByKey,
  colors,
  onChange,
  onGroupToggle,
  depth = 0,
}) => {
  const moduleKeys = collectNodeModuleKeys(node);
  const isGroupFullyEnabled =
    moduleKeys.length > 0 &&
    moduleKeys.every((key) => {
      const p = permissionsByKey.get(key);
      return Boolean(p?.canView && p?.canEdit && p?.canDelete && p?.canClose);
    });

  const isNested = depth > 0;

  return (
    <AccordionItem
      key={node.id}
      title={
        <span className="inline-flex items-center gap-2">
          {node.icon ? <span className="inline-flex">{node.icon}</span> : null}
          <span className={isNested ? 'font-medium text-sm' : 'font-medium'}>{node.title}</span>
        </span>
      }
      defaultOpen={false}
      className={isNested ? 'border-0 rounded-md bg-black/[0.02] dark:bg-white/[0.03]' : undefined}
      headerClassName={isNested ? 'px-3 py-2' : undefined}
      headerAction={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onGroupToggle(moduleKeys, !isGroupFullyEnabled)}
        >
          {isGroupFullyEnabled ? 'Disable all' : 'Enable all'}
        </Button>
      }
    >
      <div className={cn('space-y-3', isNested ? 'pl-1' : '')}>
        <PermissionTable
          entries={node.entries}
          permissionsByKey={permissionsByKey}
          colors={colors}
          onChange={onChange}
          rowKeyPrefix={node.id}
        />
        {node.children.length > 0 ? (
          <Accordion className="space-y-2">
            {node.children.map((child) => (
              <PermissionNodeAccordion
                key={child.id}
                node={child}
                permissionsByKey={permissionsByKey}
                colors={colors}
                onChange={onChange}
                onGroupToggle={onGroupToggle}
                depth={depth + 1}
              />
            ))}
          </Accordion>
        ) : null}
      </div>
    </AccordionItem>
  );
};

const UserPermissionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const userId = id ? parseInt(id, 10) : NaN;
  const { user, isLoading: userLoading } = useUser(userId);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [otherUsers, setOtherUsers] = useState<User[]>([]);
  const [copyFromUserId, setCopyFromUserId] = useState<string>('');
  const [copying, setCopying] = useState(false);

  const sidebarTree = useMemo(() => buildDynamicPermissionStructure(), []);

  const sidebarModuleKeys = useMemo(() => collectModuleKeys(sidebarTree), [sidebarTree]);

  const mergeWithSidebarKeys = (rows: PermissionRow[]): PermissionRow[] => {
    const byKey = new Map(
      rows.map((p) => [
        p.moduleKey,
        {
          ...p,
          canClose: Boolean(p.canClose ?? p.canEdit),
        },
      ]),
    );
    sidebarModuleKeys.forEach((key) => {
      if (!byKey.has(key)) {
        byKey.set(key, { moduleKey: key, canView: false, canEdit: false, canDelete: false, canClose: false });
      }
    });
    return Array.from(byKey.values());
  };

  useEffect(() => {
    if (!id || Number.isNaN(userId)) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get<PermissionRow[]>(`/users/${userId}/permissions`);
        setPermissions(mergeWithSidebarKeys(Array.isArray(res.data) ? res.data : []));
      } catch (e) {
        console.error(e);
        showError('Failed to load permissions');
        setPermissions(mergeWithSidebarKeys([]));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, id, showError, sidebarModuleKeys]);

  useEffect(() => {
    if (userLoading || !userId) return;
    const loadUsers = async () => {
      try {
        const res = await api.get<User[] | { data: User[] }>('/users');
        const list = Array.isArray(res.data) ? res.data : (res.data as { data: User[] })?.data ?? [];
        setOtherUsers(list.filter((u) => u.id !== userId));
      } catch {
        setOtherUsers([]);
      }
    };
    loadUsers();
  }, [userId, userLoading]);

  const handleCopyFromUser = async () => {
    const sourceId = copyFromUserId ? parseInt(copyFromUserId, 10) : NaN;
    if (Number.isNaN(sourceId)) {
      showError('Select a user to copy from');
      return;
    }
    try {
      setCopying(true);
      const res = await api.get<PermissionRow[]>(`/users/${sourceId}/permissions`);
      setPermissions(mergeWithSidebarKeys(Array.isArray(res.data) ? res.data : []));
      setCopyFromUserId('');
      showSuccess('Permissions copied. Review and save to apply.');
    } catch (e) {
      console.error(e);
      showError('Failed to load permissions from user');
    } finally {
      setCopying(false);
    }
  };

  const handleChange = (moduleKey: string, field: 'canView' | 'canEdit' | 'canDelete' | 'canClose', value: boolean) => {
    setPermissions((prev) =>
      prev.map((p) => (p.moduleKey === moduleKey ? { ...p, [field]: value } : p))
    );
  };

  const handleGroupToggle = (moduleKeys: string[], enabled: boolean) => {
    const keySet = new Set(moduleKeys);
    setPermissions((prev) => {
      const byKey = new Map(prev.map((p) => [p.moduleKey, { ...p }]));
      keySet.forEach((key) => {
        byKey.set(key, {
          moduleKey: key,
          canView: enabled,
          canEdit: enabled,
          canDelete: enabled,
          canClose: enabled,
        });
      });
      return Array.from(byKey.values());
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/users/${userId}/permissions`, { permissions });
      showSuccess('Permissions saved');
      navigate(-1);
    } catch (e) {
      console.error(e);
      showError('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const permissionsByKey = useMemo(() => {
    const map = new Map<string, PermissionRow>();
    permissions.forEach((p) => map.set(p.moduleKey, p));
    return map;
  }, [permissions]);

  const permissionTree = useMemo(() => {
    const used = new Set<string>();
    const filtered = sidebarTree
      .map((node) => filterNodeByPermissions(node, permissionsByKey))
      .filter((n): n is PermissionNode => n != null);

    filtered.forEach((node) => {
      collectNodeModuleKeys(node).forEach((k) => used.add(k));
    });

    const extras = Array.from(permissionsByKey.keys()).filter((k) => !used.has(k));
    if (extras.length > 0) {
      filtered.push({
        id: 'other-modules',
        title: 'Other Modules',
        entries: extras.map((key) => ({ key, label: toLabel(key) })),
        children: [],
      });
    }
    return filtered;
  }, [sidebarTree, permissionsByKey]);

  if (userLoading || !user) {
    return (
      <div className="p-6">
        <p>Loading...</p>
      </div>
    );
  }

  if (user.role === 'ADMIN') {
    return (
      <div className="p-6">
        <p className="mb-4">Admin users have full access to all modules. Permissions are not editable.</p>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl flex flex-col h-[calc(100vh-6rem)] min-h-0">
      <div className="flex items-center gap-4 mb-4 flex-shrink-0">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10"
          aria-label="Back"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold">Permissions: {user.userDisplayName || user.userEmail}</h1>
          <p className="text-sm opacity-80">View, Edit, and Delete per module — groups match the sidebar</p>
        </div>
        <div className={cn('flex flex-wrap items-center gap-2 flex-shrink-0', colors.textMuted)}>
          <span className="text-sm">Copy from:</span>
          <select
            value={copyFromUserId}
            onChange={(e) => setCopyFromUserId(e.target.value)}
            disabled={copying || otherUsers.length === 0}
            className={cn(
              'rounded border px-2 py-1.5 text-sm min-w-[180px]',
              colors.border,
              'bg-white dark:bg-gray-800',
              colors.text
            )}
            aria-label="Select user to copy permissions from"
          >
            <option value="">Select user…</option>
            {otherUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.userDisplayName || `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() || u.userEmail}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleCopyFromUser}
            disabled={!copyFromUserId || copying}
          >
            {copying ? 'Copying...' : 'Copy'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p>Loading permissions...</p>
      ) : (
        <>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1 -mr-1">
            <Accordion className="space-y-2 pb-4">
              {permissionTree.map((node) => (
                <PermissionNodeAccordion
                  key={node.id}
                  node={node}
                  permissionsByKey={permissionsByKey}
                  colors={colors}
                  onChange={handleChange}
                  onGroupToggle={handleGroupToggle}
                />
              ))}
            </Accordion>
          </div>
          <div className={`mt-4 flex gap-2 flex-shrink-0 pt-2 border-t ${colors.border}`}>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save permissions'}
            </Button>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default UserPermissionsPage;
